import format from "date-fns/format";
import helpers from "@/helpers/global";
import _ from "lodash";
import { safeGetItem, safeSetItem, safeRemoveItem } from "@/utils/storage";
import {
  clearSentryUserContext,
  syncSentrySessionFromStorage,
} from "@/utils/sentryUserContext";
import { isBenignFirebaseAuthPopupError } from "@/utils/firebaseAuthErrors";
import { isAuthVerifyApiEnabled } from "@/utils/authVerifyConfig";
import { isSignupEmailRegistered } from "@/utils/signupEmailCheck";
import { forgetActiveTeam, rememberActiveTeam } from "@/utils/lastActiveTeam";
import { handOffLoginProfile } from "@/utils/loginProfileHandoff";
import {
  postAuthLoginRequest,
  postAuthLoginVerify,
  postAuthSignupRequest,
  postAuthSignupVerify,
  postAuthGuestVerify,
} from "@/helpers/authVerifyApi";
import {
  getPostLoginRedirect,
  savePostLoginRedirect,
  takePostLoginRedirect,
} from "@/helpers/postLoginRedirect";
import { Auth, Provider, SignupFormDetails, User } from "../types";
const DATETIME_FORMAT = "yyy-MM-dd HH:mm:ss";

const itemKey = "signupFormDetails";

/** Local dev only: load another user's RTDB profile after OTP (see VUE_APP_DEV_LOGIN_EMAIL). */
function resolveDevLoginEmail(email: string): string {
  const override = String(process.env.VUE_APP_DEV_LOGIN_EMAIL || "").trim();
  if (override && process.env.NODE_ENV === "development") {
    return helpers.filterEmail(override);
  }
  return email;
}

async function loadFirebaseDatabaseDeps() {
  return import("firebase/database");
}

async function loadFirebaseAuthDeps() {
  const [databaseModule, authModule] = await Promise.all([
    import("firebase/database"),
    import("firebase/auth"),
  ]);

  return {
    ...databaseModule,
    ...authModule,
  };
}

export type LoginErrorReason =
  | "no_account"
  | "use_google"
  | "use_microsoft"
  | "closed"
  | "send_failed";

async function ensureStoreModule(moduleName: "guest" | "user") {
  const { ensureModule } = await import("@/store");
  await ensureModule(moduleName);
}

export default {
  namespaced: true,
  state: {
    user: null,
    error: null,
    otpServiceError: false,
    // Why the last login request was refused, so the form can offer the
    // right next step (sign up, use Google, ...). See requestLoginWeb.
    loginErrorReason: null,
    selectedProvider: null,
    email: null,
    loading: false,
    signupFormDetails: null,
    redirectUrl: getPostLoginRedirect("/"),
  },
  mutations: {
    otpServiceError(state: { otpServiceError: boolean }, value: boolean) {
      state.otpServiceError = value;
    },
    loginErrorReason(
      state: { loginErrorReason: LoginErrorReason | null },
      value: LoginErrorReason | null
    ) {
      state.loginErrorReason = value;
    },
    setUserLogin(state: Auth, user: User): any {
      state.user = user;
    },
    errorMessage(state: Auth, error: string): any {
      state.error = error;
    },
    selectedProvider(state: Auth, selectedProvider: Provider): any {
      state.selectedProvider = selectedProvider;
    },
    setEmail(state: Auth, email: string): any {
      state.email = email;
    },
    loading(state: { loading: boolean }, loading: boolean): any {
      state.loading = loading;
    },
    signupFormDetails(state: Auth, signupFormDetails: SignupFormDetails): any {
      state.signupFormDetails = signupFormDetails;
    },
    redirectUrl(state: Auth, redirectUrl: string): any {
      state.redirectUrl = savePostLoginRedirect(redirectUrl);
    },
  },
  getters: {
    otpServiceError(state: { otpServiceError: boolean }): boolean {
      return state.otpServiceError;
    },
    user(state: Auth): any {
      return state.user;
    },
    error(state: Auth): string {
      return state.error;
    },
    loginErrorReason(state: {
      loginErrorReason: LoginErrorReason | null;
    }): LoginErrorReason | null {
      return state.loginErrorReason;
    },
    selectedProvider(state: Auth): Provider {
      return state.selectedProvider;
    },
    email(state: Auth): string {
      return state.email;
    },
    signupFormDetails(state: Auth): SignupFormDetails {
      return state.signupFormDetails;
    },
    redirectUrl(state: Auth): string {
      return state.redirectUrl;
    },
  },
  actions: {
    async setUserLogin({ commit }: { commit: any }): Promise<any> {
      const userData: any = safeGetItem("userData");
      if (userData) {
        try {
          // commit("setUserLogin", JSON.parse(atob(userData) || "{}"));
          commit(
            "setUserLogin",
            JSON.parse(
              decodeURIComponent(escape(atob(String(userData)))) || "{}"
            )
          );
        } catch (error) {
          try {
            const { getAuth } = await loadFirebaseAuthDeps();
            await getAuth().signOut();
          } catch (signOutError) {
            if (!isBenignFirebaseAuthPopupError(signOutError)) {
              console.warn("[auth/setUserLogin] signOut failed", signOutError);
            }
          }
          safeRemoveItem("userData");
          commit("setUserLogin", null);

          // @todo redirect to page by user's role
          window.location.replace("/login");
        }
      }
    },
    clearError({ commit }: { commit: any }): any {
      commit("errorMessage", null);
    },
    async addLogs({ commit }: { commit: any }): Promise<any> {
      // let ip = "";
      // const url = "https://api.ipify.org?format=json"
      // await axios.get(url).then(response => {
      //   ip = response.data.ip
      // })
      // .catch(function (error) {
      //     console.log(error);
      // });
      // object.ip = ip;
      // await firebase.firestore().collection("logs").add(object);
      // return true;
      // console.log(object);
      commit("errorMessage", null);
    },
    async loginByOtp(
      { commit, dispatch }: { commit: any; dispatch: any },
      params: { email: string; pincode: string }
    ): Promise<any> {
      commit("otpServiceError", false);
      const email = helpers.filterEmail(params.email);
      const loginEmail = resolveDevLoginEmail(params.email);

      // Fetch the user record while the code is being checked, so
      // loginAsUser doesn't pay for another round trip after sign-in. If the
      // read fails, loginAsUser falls back to reading it itself.
      const userRecordPromise: Promise<any> = loadFirebaseDatabaseDeps()
        .then(({ getDatabase, ref, query, get }) => {
          const key = helpers.filterPath(helpers.filterEmail(loginEmail));
          return get(query(ref(getDatabase(), `users/${key}`)));
        })
        .then((snap) => snap.val())
        .catch(() => null);

      // The code was right, so a failure here is not a wrong-code attempt:
      // flag it as a service error so the Pincode screen says so.
      const finishOtpLogin = async (): Promise<boolean> => {
        const loggedIn = await dispatch("loginAsUser", {
          email: loginEmail,
          userRecord: await userRecordPromise,
          // Sign-in has already saved the session, so there is nothing to
          // wait for before the dashboard loads.
          redirectDelayMs: 0,
        });
        if (!loggedIn) {
          commit("otpServiceError", true);
        }
        return Boolean(loggedIn);
      };

      if (isAuthVerifyApiEnabled()) {
        try {
          const { data } = await postAuthLoginVerify({
            email,
            pincode: params.pincode,
          });

          if (!data.verified || !data.customToken) {
            commit(
              "errorMessage",
              data.message || "Invalid email or OTP",
            );
            return false;
          }

          commit("errorMessage", "");
          const { getAuth, signInWithCustomToken } =
            await loadFirebaseAuthDeps();
          await signInWithCustomToken(getAuth(), data.customToken);
          return await finishOtpLogin();
        } catch (err) {
          console.error("[auth/loginByOtp] auth-verify API failed", err);
          commit("otpServiceError", true);
          commit("errorMessage", helpers.t("createAccount.Otp_service_error"));
          return false;
        }
      }

      const { getDatabase, ref, query, get } = await loadFirebaseDatabaseDeps();
      const emailKey = helpers.filterPath(email);
      // const emailKey = helpers.filterPath("a@b.com");

      const db = getDatabase();
      const path = `login_send_email_web/${emailKey}`;
      const data = (await get(query(ref(db, path)))).val();
      const result = _.isEmpty(data) ? false : data.pincode == params.pincode;

      if (!result) {
        commit("errorMessage", "Invalid email or OTP");
        return false;
      }

      commit("errorMessage", "");
      // Sync Firebase Auth with app session: signup uses password === email (see user/createUser).
      const { getAuth, signInWithEmailAndPassword } =
        await loadFirebaseAuthDeps();
      try {
        await signInWithEmailAndPassword(getAuth(), email, email);
      } catch (e: unknown) {
        const err = e as { code?: string; message?: string };
        console.warn(
          "[auth/loginByOtp] signInWithEmailAndPassword failed; Firebase session may not match app user:",
          err?.code || err?.message || e
        );
      }
      return await finishOtpLogin();
    },
    async loginAsUser(
      {
        commit,
        dispatch,
        getters,
        rootState,
      }: { commit: any; dispatch: any; getters: any; rootState: any },
      {
        email,
        callback,
        userRecord,
        redirectDelayMs = 300,
      }: {
        email: string;
        callback?: any;
        userRecord?: any;
        redirectDelayMs?: number;
      }
    ): Promise<any> {
      const { getDatabase, ref, query, get } = await loadFirebaseDatabaseDeps();
      email = helpers.filterEmail(email);
      const emailKey = helpers.filterPath(email);

      // Callers that just wrote the record (signup) can pass it in to skip a
      // redundant round trip.
      const db = getDatabase();
      const path = `users/${emailKey}`;
      const user =
        userRecord && !_.isEmpty(userRecord)
          ? userRecord
          : (await get(query(ref(db, path)))).val();
      const createdAt = format(new Date(), DATETIME_FORMAT);

      // @todo if email is mobile phone number

      if (!_.isEmpty(user)) {
        // Closed (soft-deleted) accounts cannot be logged back in to.
        if (user.deleted) {
          commit("loading", false);
          commit("errorMessage", helpers.t("deleteAccount.login_rejected"));
          return false;
        }
        const userData = {
          email: user.email,
          firstname: user.firstName,
          lastname: user.lastName,
          displayName: user.displayName,
          companyName: user.companyName,
          companyLogoUrl: user.companyLogourl,
          color: user.color,
          photoUrl: user.photoURL,
          coreValues: user.coreValues || [],
          typeOfOrg: user.typeOfOrg || "",
          missionAndVision: user.missionAndVision || "",
          autoGenerateForm: user.autoGenerateForm || false,
          isGuest: user.isGuest || false,
          createdAt: createdAt,
        };
        safeSetItem(
          "userData",
          btoa(unescape(encodeURIComponent(JSON.stringify(userData))))
        );
        syncSentrySessionFromStorage();
        // The dashboard after this reads the team alongside the profile
        // instead of after it (see lastActiveTeam).
        if (user.teamId) rememberActiveTeam(user.email || email, user.teamId);

        // @todo log access data
        // commit("errorMessage", "");
        commit("loading", false);

        // @todo route to default page
        const redirectUrl = takePostLoginRedirect(
          getters["redirectUrl"] || "/"
        );

        // @fixed save coreValues if came from analyzer
        let profileChanged = false;
        if (rootState.coreValuesAnalyzer) {
          if (rootState.coreValuesAnalyzer.doSaveValues) {
            const values = rootState.coreValuesAnalyzer.values;
            if (values) {
              commit("setUserLogin", userData);
              await dispatch("coreValuesAnalyzer/saveValues", values, {
                root: true,
              });
              profileChanged = true;
            }
          }
        }

        // delay for Firebase auth state propagation (reduced from 1500ms)
        if (callback && _.isFunction(callback)) {
          callback();
        } else {
          // The page after the reload uses this record instead of reading
          // it again, unless it was just changed above.
          if (!profileChanged) handOffLoginProfile(email, user);
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, redirectDelayMs);
        }
        return true;
      } else {
        console.log(`Not found user email: ${email}`);
        return false;
      }

      /*
      // @see src\store\core\authen.ts
      const emailKey = targetEmail.replace(/\./g, "||").toLowerCase()
      const user = (await firebase.database().ref(`/users/${emailKey}`).once("value")).val()
      const company = (await firebase.database().ref(`/company/${user.company}`).once("value")).val()
      const userData = {
        company_name: company.name,
        company: user.company,
        firstname: user.firstname,
        lastname: user.lastname,
        user_type: user.user_type,
        email: user.email,
        profilepic: (user.profilepic) ? user.profilepic : "",
        profilepic_crop: (user.profilepic_crop) ? user.profilepic_crop : "",
        profilepic_small: (user.profilepic_small) ? user.profilepic_small : ""
      }

      safeSetItem("userData", btoa(JSON.stringify(userData)))

      dispatch("authen/addLogs", {
        createdate: format(new Date(), "YYYY-MM-DD HH:mm:ss"),
        status: "login",
        user: user.email
      }, { root: true }).then(function () {
        setTimeout(() => {
          // console.log("loginAsUser(): success")
          // commit("loadingStatus", false)

          // @fixed prevent error from many components
          // @todo  use route instead redirect
          // @see   router.replace("/")

          // @todo redirect IS NOT elegant display
          const redirectUrl = user.user_type == "admin" ? "/members" : "/"
          window.location.href = redirectUrl
          // router.replace(redirectUrl)

        }, 500)
      })
      //*/
    },
    async login(
      { dispatch }: { dispatch: any },
      authData: { email: string; provider: string }
    ): Promise<any> {
      const provider = authData.provider;
      dispatch(`${provider}/login`, authData, { root: true });
    },
    async bindAccount(
      { dispatch }: { dispatch: any },
      authData: { email: string; provider: string; success?: any; error?: any }
    ): Promise<any> {
      const provider = authData.provider;
      dispatch(`${provider}/bindAccount`, authData, { root: true });
    },
    async signup(
      { dispatch }: { dispatch: any },
      authData: { email: string; provider: string }
    ): Promise<any> {
      const provider = authData.provider;
      dispatch(`${provider}/signup`, authData, { root: true });
    },
    async logout({ commit }: { commit: any }): Promise<any> {
      // unregister all service worker(s)
      if (window.navigator && navigator.serviceWorker) {
        navigator.serviceWorker
          .getRegistrations()
          .then(function (registrations) {
            for (const registration of registrations) {
              registration.unregister();
            }
          });
      }

      safeRemoveItem("userVersion");
      safeRemoveItem("userData");
      safeRemoveItem("cachedCompanyColor");
      safeRemoveItem("cachedCompanyLogoUrl");
      forgetActiveTeam();
      commit("setUserLogin", null);
      clearSentryUserContext();

      try {
        const { getAuth } = await loadFirebaseAuthDeps();
        await getAuth().signOut();
      } catch (e) {
        if (!isBenignFirebaseAuthPopupError(e)) {
          console.warn("[auth/logout] signOut failed", e);
        }
      }

      // Drop the Firestore IndexedDB cache so org/user data doesn't outlive
      // the session on shared computers. clearIndexedDbPersistence only runs
      // against a terminated instance, and it can fail legitimately (e.g.
      // another tab still holds the database) — never block the redirect.
      try {
        const { terminate, clearIndexedDbPersistence  } = await import("firebase/firestore");
        const { getAppFirestore } = await import("@/utils/appFirestore");
        const db = getAppFirestore();
        await terminate(db);
        await clearIndexedDbPersistence(db);
      } catch (e) {
        console.warn("[auth/logout] failed to clear Firestore cache", e);
      }

      // @todo config of page after logout
      window.location.replace("/");
    },
    async requestLoginWeb(
      { commit, dispatch }: { commit: any; dispatch: any },
      loginWebRequest: {
        email: string;
      }
    ): Promise<any> {
      const { getDatabase, ref, query, get, set, push, child } =
        await loadFirebaseDatabaseDeps();
      await ensureStoreModule("user");
      const email = helpers.filterEmail(loginWebRequest.email);
      const emailKey = helpers.filterPath(email);

      /*
       * @todo validate eamil
       * @see https://trello.com/c/1JuD1WaP/1290-assessment-as-a-user-i-want-to-be-able-to-log-in-to-my-account-so-that-i-can-manage-my-assessments
       *
       * 1. If user signed up using an email address method but tries to login using Google or Microsoft Authen, allow the user to switch login method
       * 2. If user signed up using Google or Microsoft Authen but tries to login using normal email, display an error message
       */

      const db = getDatabase();
      const refuse = (reason: LoginErrorReason, message?: string) => {
        commit("loginErrorReason", reason);
        commit("errorMessage", message || helpers.t(`loginError.${reason}`));
        return false;
      };
      commit("loginErrorReason", null);

      // The four pre-checks are independent reads; run them in parallel so a
      // login attempt costs one round trip instead of four. Results are still
      // evaluated in the original order to keep error-message precedence.
      const googleUserPath = `oauth_users/google/${emailKey}`;
      const microsoftUserPath = `oauth_users/microsoft/${emailKey}`;
      const [googleUserSnap, microsoftUserSnap, hasUser, userRecordSnap] =
        await Promise.all([
          get(query(ref(db, googleUserPath))),
          // @todo check microsoft. Can't checking now because of email format
          // ex: rcoco66_hotmail||com||ext||@rcoco66hotmail||onmicrosoft||com
          get(query(ref(db, microsoftUserPath))),
          dispatch("user/hasUser", email, { root: true }),
          get(query(ref(db, `users/${emailKey}`))),
        ]);

      // google
      if (googleUserSnap.val()) {
        return refuse("use_google");
      }

      // microsoft
      if (microsoftUserSnap.val()) {
        return refuse("use_microsoft");
      }

      // has user in system
      if (!hasUser) {
        return refuse("no_account");
      }

      // Reject closed (soft-deleted) accounts before sending an OTP.
      const userRecord = userRecordSnap.val();
      if (userRecord && userRecord.deleted) {
        return refuse("closed", helpers.t("deleteAccount.login_rejected"));
      }

      const hostname =
        typeof window !== "undefined" ? window.location.hostname : "";

      if (isAuthVerifyApiEnabled()) {
        try {
          const { data } = await postAuthLoginRequest({ email, hostname });
          if (data.sent) {
            commit("setEmail", email);
            commit("errorMessage", null);
            return true;
          }
          return refuse("send_failed", data.message);
        } catch (err) {
          console.error("[auth/requestLoginWeb] auth-verify API failed", err);
          return refuse("send_failed");
        }
      }

      const createdAt = helpers.now();
      const $loginWebRequest = {
        ...loginWebRequest,
        createdAt: createdAt,
        hostname,
      };

      // send the request to server for get OTP
      const otpPath = `loginweb`;
      const otpKey = push(child(ref(db), otpPath)).key;

      let result = false;
      await set(ref(db, "loginweb/" + otpKey), $loginWebRequest)
        .then(() => {
          // Data saved successfully!
          // console.log("Data saved successfully!");
          commit("setEmail", email);
          commit("errorMessage", null);
          result = true;
        })
        .catch((error) => {
          // The write failed...
          console.log(error, "error");
          refuse("send_failed");
        });

      return result;
    },
    async requestSignupWeb(
      { commit }: { commit: any },
      {
        email,
      }: {
        email: string;
      }
    ): Promise<any> {
      const [{ getDatabase, ref, set, push, child }, alreadyRegistered] =
        await Promise.all([
          loadFirebaseDatabaseDeps(),
          isSignupEmailRegistered(email),
        ]);
      const db = getDatabase();
      if (alreadyRegistered) {
        const error = `this email (${email}) has already been registered`;
        commit("errorMessage", error);
        console.error(error);
        return;
      }
      commit("errorMessage", "");

      const lang = helpers.getLanguage() || "en";
      const hostname =
        typeof window !== "undefined" ? window.location.hostname : "";

      if (isAuthVerifyApiEnabled()) {
        try {
          const { data } = await postAuthSignupRequest({
            email,
            hostname,
            lang,
          });
          if (data.sent) {
            commit("setEmail", email);
            commit("errorMessage", "");
            return;
          }
          commit(
            "errorMessage",
            data.message ||
              "Unable to send a verification code. This email may already be registered.",
          );
        } catch (err) {
          console.error("[auth/requestSignupWeb] auth-verify API failed", err);
          commit(
            "errorMessage",
            "Unable to send a verification code. Please try again later.",
          );
        }
        return;
      }

      // send the request to server for get OTP
      const otpPath = `signupweb`;
      const otpKey = push(child(ref(db), otpPath)).key;

      const createdAt = helpers.now();
      const $signupWebRequest = {
        email: email,
        lang: lang,
        createdAt: createdAt,
        hostname,
      };

      await set(ref(db, otpPath + "/" + otpKey), $signupWebRequest)
        .then(() => {
          // Data saved successfully!
          commit("setEmail", email);
          commit("errorMessage", "");
        })
        .catch((error) => {
          // The write failed...
          commit("errorMessage", error);
          console.error(error);
        });
    },
    async verifySignupOtp(
      { commit }: { commit: any },
      params: { email: string; pincode: string }
    ): Promise<any> {
      commit("otpServiceError", false);
      const email = helpers.filterEmail(params.email);

      if (isAuthVerifyApiEnabled()) {
        try {
          const { data } = await postAuthSignupVerify({
            email,
            pincode: params.pincode,
          });
          if (!data.verified) {
            commit(
              "errorMessage",
              data.message || "Invalid email or OTP",
            );
            return false;
          }
          commit("errorMessage", "");
          return true;
        } catch (err) {
          console.error("[auth/verifySignupOtp] auth-verify API failed", err);
          commit("otpServiceError", true);
          commit("errorMessage", helpers.t("createAccount.Otp_service_error"));
          return false;
        }
      }

      const { getDatabase, ref, query, get } = await loadFirebaseDatabaseDeps();
      const emailKey = helpers.filterPath(email);

      const db = getDatabase();
      const path = `signup_send_email_web/${emailKey}`;
      const data = (await get(query(ref(db, path)))).val();
      const result = _.isEmpty(data) ? false : data.pincode == params.pincode;

      if (!result) {
        commit("errorMessage", "Invalid email or OTP");
        return false;
      }

      commit("errorMessage", "");
      return true;
    },
    async verifyGuestWeb(
      { commit, getters }: { commit: any; getters: any },
      {
        email,
      }: {
        email: string;
      }
    ): Promise<any> {
      const { getDatabase, ref, query, get, set, push, child } =
        await loadFirebaseDatabaseDeps();
      // If click this verified button before invite the email is empty
      if (!email) {
        commit(
          "errorMessage",
          "The email is required. Please invite the email or verify with Google or MSFT"
        );
        return;
      }

      const emailKey = helpers.filterPath(helpers.filterEmail(email));
      const db = getDatabase();
      const path = `users/${emailKey}`;
      const user = (await get(query(ref(db, path)))).val();
      if (user) {
        const error = `This email (${email}) has already been registered`;
        commit("errorMessage", error);
        return;
      }

      const guest = getters["user"];
      // console.log(guest, "guest");
      // if (getters) return;

      const guestEmail = _.get(guest, "email", "");
      if (!guestEmail) {
        commit("errorMessage", "Please login to verify your email");
        return;
      }

      // send the request to server for get OTP
      const otpPath = `verifyweb`;
      const otpKey = push(child(ref(db), otpPath)).key;

      const createdAt = helpers.now();
      const $guestWebData = {
        email: email,
        guestEmail: guestEmail,
        createdAt: createdAt,
      };

      await set(ref(db, otpPath + "/" + otpKey), $guestWebData)
        .then(() => {
          // Data saved successfully!
          // commit("setEmail", email);
          commit("errorMessage", "");
        })
        .catch((error) => {
          // The write failed...
          commit("errorMessage", error);
          console.error(error);
        });
    },
    async verifyGuestOtp(
      {
        commit,
        dispatch,
        getters,
      }: { commit: any; dispatch: any; getters: any },
      params: { email: string; pincode: string }
    ): Promise<any> {
      const { getDatabase, ref, query, get } = await loadFirebaseDatabaseDeps();
      await Promise.all([ensureStoreModule("user"), ensureStoreModule("guest")]);
      const email = helpers.filterEmail(params.email);
      const emailKey = helpers.filterPath(email);

      const currentUser = getters["user"];
      if (!currentUser) {
        commit(
          "errorMessage",
          "You are not a guest account. Please login with guest account to verify your email"
        );
        return;
      }

      if (!currentUser.isGuest) {
        commit(
          "errorMessage",
          "You are not a guest account. Please login with guest account to verify your email"
        );
        return;
      }

      if (isAuthVerifyApiEnabled()) {
        commit("otpServiceError", false);
        // Check first: the API burns the single-use PIN when it verifies.
        const existingUser = await dispatch("user/getUser", email, {
          root: true,
        });
        if (existingUser) {
          commit(
            "errorMessage",
            helpers.t("createAccount.email_registered", { email })
          );
          return false;
        }

        try {
          const { data } = await postAuthGuestVerify({
            email,
            pincode: params.pincode,
            guestEmail: currentUser.email,
          });
          if (!data.verified) {
            commit(
              "errorMessage",
              data.error === "guest_mismatch"
                ? "You are not the correct guest account"
                : data.message || helpers.t("popup.message.message_7e")
            );
            return false;
          }
        } catch (err) {
          console.error("[auth/verifyGuestOtp] auth-verify API failed", err);
          commit("otpServiceError", true);
          commit("errorMessage", helpers.t("createAccount.Otp_service_error"));
          return false;
        }

        await dispatch(
          "guest/transferGuestToUser",
          { guestEmail: currentUser.email, email },
          { root: true }
        );
        commit("errorMessage", "");
        return true;
      }

      const db = getDatabase();
      const path = `verify_send_email_web/${emailKey}`;
      const data = (await get(query(ref(db, path)))).val();
      const result = _.isEmpty(data) ? false : data.pincode == params.pincode;

      if (!result) {
        commit("errorMessage", helpers.t("popup.message.message_7e"));
        return false;
      }

      // Is the same guest?
      if (data.guestEmail !== currentUser.email) {
        commit("errorMessage", "You are not the correct guest account");
        return false;
      }

      // Already exists user?
      const targetUser = await dispatch("user/getUser", email, {
        root: true,
      });
      if (targetUser) {
        commit(
          "errorMessage",
          helpers.t("createAccount.email_registered", { email })
        );
        return false;
      }

      await dispatch(
        "guest/transferGuestToUser",
        {
          guestEmail: data.guestEmail,
          email: email,
        },
        { root: true }
      );

      commit("errorMessage", "");
      return true;
    },
    initSignupFormDetails({
      state,
      commit,
    }: {
      state: Auth;
      commit: any;
    }): any {
      const signupFormDetails = state.signupFormDetails;
      if (_.isEmpty(signupFormDetails)) {
        if (safeGetItem(itemKey)) {
          const restoredData = JSON.parse(
            decodeURIComponent(
              escape(atob(String(safeGetItem(itemKey))))
            ) || "{}"
          );
          if (!_.isEmpty(restoredData)) {
            commit(itemKey, restoredData);
          }
        }
      }
    },
    // @todo move to sign up store
    clearSignupFormDetails({ commit }: { commit: any }): any {
      commit(itemKey, null);
      safeRemoveItem(itemKey);
    },
    // @todo move to sign up store
    async applySignupFormDetails(
      { state, commit }: { state: Auth; commit: any },
      formDetails: SignupFormDetails
    ): Promise<any> {
      let restoredData = {};
      if (safeGetItem(itemKey)) {
        restoredData = JSON.parse(
          decodeURIComponent(
            escape(atob(String(safeGetItem(itemKey))))
          ) || "{}"
        );
      }

      const signupFormDetails = _.merge(
        restoredData,
        state.signupFormDetails || {},
        formDetails
      );
      commit("signupFormDetails", signupFormDetails);

      safeSetItem(
        "signupFormDetails",
        btoa(unescape(encodeURIComponent(JSON.stringify(signupFormDetails))))
      );
    },
    // @todo move to sign up store
    async setSignupEmail(
      { dispatch }: { dispatch: any },
      formDetails: SignupFormDetails
    ): Promise<any> {
      dispatch("applySignupFormDetails", formDetails);
    },
    // @todo move to sign up store
    async setSignupInfo(
      { dispatch }: { dispatch: any },
      formDetails: SignupFormDetails
    ): Promise<any> {
      dispatch("applySignupFormDetails", formDetails);
    },
    // @todo move to sign up store
    async setSignupBranding(
      { dispatch }: { dispatch: any },
      formDetails: SignupFormDetails
    ): Promise<any> {
      dispatch("applySignupFormDetails", formDetails);
    },
    async setLoginEmail(
      { commit }: { commit: any },
      formDetails: { email: string }
    ): Promise<any> {
      commit("setEmail", formDetails.email);
      safeSetItem("loginEmail", formDetails.email);
    },
  },
};
